import {
  ArrowRight,
  BadgeCheck,
  BarChart3,
  BookOpenCheck,
  Check,
  ChevronDown,
  CircleAlert,
  CircleDollarSign,
  ClipboardCheck,
  Eye,
  Gauge,
  LayoutDashboard,
  LogOut,
  Menu,
  PackageCheck,
  Radar,
  ReceiptText,
  ShieldCheck,
  Sparkles,
  UserRoundCog,
  WalletCards,
  X,
} from "lucide-react";
import {
  type FormEvent,
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useState,
} from "react";
import {
  BrowserRouter,
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import {
  channelLabels,
  channelPlatforms,
  type ChannelPlatform,
  type ChannelProfile,
  type ChannelProfileInput,
  type Recommendation,
} from "../shared/contracts";
import { formatExpectedOrderSignal } from "../shared/presentation";
import { apiRequest, formatCurrency, formatPercent } from "./api";
import { AuthProvider, useAuth } from "./auth";

type RecommendationPayload = {
  recommendations: Recommendation[];
  profile: ChannelProfile | null;
  catalogMode: "demo" | "taobao";
  recommendationDay?: string;
  dailyLocked?: boolean;
  awaitingProfileSelection?: boolean;
  baseline?: {
    exposure: number;
    clicks: number;
    paidOrders: number;
    settledOrders: number;
    publishedCount: number;
  };
};

type Handoff = {
  id: string;
  productId: string;
  channelProfileId: string;
  externalId: string;
  productTitle: string;
  channelName: string;
  dataOrigin: "synthetic_demo" | "taobao_official_api";
  createdAt: number;
};

type OutcomeRecord = {
  id: string;
  handoffEventId: string;
  productId: string;
  productTitle: string;
  channelProfileId: string;
  channelName: string;
  dataOrigin: "synthetic_demo" | "taobao_official_api";
  status: "candidate" | "published" | "settled" | "cancelled";
  contentUrl: string | null;
  publishedAt: number | null;
  actualExposure: number;
  clicks: number;
  paidOrders: number;
  settledOrders: number;
  settledCommissionCents: number;
  sampleCostCents: number;
  contentCostCents: number;
  createdAt: number;
  updatedAt: number;
};

type OutcomeSummary = {
  promotedCount: number;
  publishedCount: number;
  settledCount: number;
  totalExposure: number;
  paidOrders: number;
  settledOrders: number;
  settledCommissionCents: number;
  knownCostsCents: number;
  contributionCents: number;
};

const scenarioLabels = {
  conservative: "保守",
  reference: "参考",
  optimistic: "乐观",
} as const;

function Brand() {
  return (
    <Link className="brand" to="/" aria-label="三选首页">
      <span className="brand__mark"><Radar size={19} /></span>
      <span><strong>三选</strong><small>创作者选品雷达</small></span>
    </Link>
  );
}

function formatDate(timestamp: number): string {
  return new Intl.DateTimeFormat("zh-CN", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(timestamp));
}

function ProductVisual({ recommendation }: { recommendation: Recommendation }) {
  if (recommendation.imageUrl && recommendation.dataOrigin === "taobao_official_api") {
    return <img className="product-visual__image" src={recommendation.imageUrl} alt="" loading="lazy" referrerPolicy="no-referrer" />;
  }
  return (
    <div className="product-visual__placeholder" aria-hidden="true">
      <span>{recommendation.title.slice(0, 1)}</span>
      <small>合成演示</small>
    </div>
  );
}

function ProductCard({
  recommendation,
  actionLabel,
  onAction,
  pending = false,
}: {
  recommendation: Recommendation;
  actionLabel?: string;
  onAction?: (recommendation: Recommendation) => void;
  pending?: boolean;
}) {
  const finalPrice = recommendation.priceCents - recommendation.couponCents;
  const reference = recommendation.scenarios.find((scenario) => scenario.name === "reference");

  return (
    <article className="product-card">
      <div className="product-card__top">
        <ProductVisual recommendation={recommendation} />
        <div className="product-card__heading">
          <div className="tag-row">
            <span className={recommendation.dataOrigin === "synthetic_demo" ? "tag tag--demo" : "tag tag--live"}>
              {recommendation.dataOrigin === "synthetic_demo" ? "合成演示数据" : "淘宝联盟 API"}
            </span>
            <span className="tag tag--muted">{recommendation.channelProfile.officialComponentConfirmed ? "渠道组件已自检" : "渠道待确认"}</span>
          </div>
          <h3>{recommendation.title}</h3>
          <p>{recommendation.category} · 近 30 天销量 {recommendation.recentSales.toLocaleString("zh-CN")} · 商品 ID {recommendation.externalId}</p>
        </div>
        <span className="score"><strong>{recommendation.score}</strong><small>适配分</small></span>
      </div>

      <div className="commission-strip">
        <div><span>券后价</span><strong>{formatCurrency(finalPrice)}</strong></div>
        <div><span>标称佣金</span><strong>{formatPercent(recommendation.commissionRateBps / 10_000)}</strong></div>
        <div><span>标称单佣</span><strong>{formatCurrency(recommendation.grossCommissionPerOrderCents)}</strong></div>
        <div className="commission-strip__primary"><span>预计结算后 / 单</span><strong>{formatCurrency(recommendation.expectedSettledCommissionPerOrderCents)}</strong></div>
      </div>

      <div className="scenario-grid">
        {recommendation.scenarios.map((scenario) => (
          <div key={scenario.name} className={scenario.name === "reference" ? "scenario scenario--reference" : "scenario"}>
            <span>{scenarioLabels[scenario.name]}</span>
            <strong>{formatCurrency(scenario.estimatedSettledCommissionCents)}</strong>
            <small>{formatExpectedOrderSignal(scenario.expectedPaidOrders)}</small>
            <small className="scenario__net">扣已知成本后 {formatCurrency(scenario.estimatedNetCents)}</small>
          </div>
        ))}
      </div>

      <div className="product-insight">
        <p><Sparkles size={16} /><span><strong>为什么适合：</strong>{recommendation.fitReason}</span></p>
        <p><BookOpenCheck size={16} /><span><strong>内容角度：</strong>{recommendation.contentAngle}</span></p>
        <p className="product-insight__risk"><CircleAlert size={16} /><span><strong>最大风险：</strong>{recommendation.riskNote}</span></p>
      </div>

      <details className="calculation">
        <summary>查看计算过程 <ChevronDown size={15} /></summary>
        <div className="calculation__body">
          <p>预计结算佣金 = 有效曝光 × 点击率 × 支付转化率 × 标称单笔佣金 ×（1 − 退款/无效率）</p>
          <p>订单只能是整数；小于 1 单的数学期望会换算成“长期平均多少条相似内容约 1 单”。</p>
          {reference && (
            <dl>
              <div><dt>有效曝光</dt><dd>{recommendation.channelProfile.averageExposure.toLocaleString("zh-CN")}</dd></div>
              <div><dt>参考点击率</dt><dd>{formatPercent(reference.clickRate)}</dd></div>
              <div><dt>参考支付转化</dt><dd>{formatPercent(reference.paymentConversionRate)}</dd></div>
              <div><dt>退款/无效率</dt><dd>{formatPercent(reference.invalidRate)}</dd></div>
              <div><dt>样品成本</dt><dd>{formatCurrency(recommendation.sampleCostCents)}</dd></div>
              <div><dt>内容成本</dt><dd>{recommendation.channelProfile.contentCostCents > 0 ? formatCurrency(recommendation.channelProfile.contentCostCents) : "未计创作时间成本"}</dd></div>
            </dl>
          )}
          <small>数据更新：{formatDate(recommendation.observedAt)} · 置信度：{recommendation.confidence === "low" ? "低" : recommendation.confidence === "medium" ? "中" : "高"}</small>
        </div>
      </details>

      <div className="product-card__footer">
        <p>{recommendation.disclosure}</p>
        {onAction && (
          <button className="button button--dark" disabled={pending} onClick={() => onAction(recommendation)}>
            {pending ? "处理中…" : actionLabel ?? "加入候选"} <ArrowRight size={16} />
          </button>
        )}
      </div>
      <p className="product-card__disclaimer">以上不是收益承诺，“乐观”不代表大概率发生。</p>
    </article>
  );
}

function PublicHeader() {
  const { viewer } = useAuth();
  return (
    <header className="public-header">
      <Brand />
      <nav>
        <a href="#how">怎么算</a>
        <a href="#demo">看演示</a>
        <a href="#pricing">价格</a>
        <Link className="button button--small" to={viewer ? "/app" : "/login"}>
          {viewer ? "进入雷达" : "免费试用"}
        </Link>
      </nav>
    </header>
  );
}

function LandingPage() {
  const { viewer } = useAuth();
  const [platform, setPlatform] = useState<ChannelPlatform>("zhihu");
  const [exposure, setExposure] = useState(30_000);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    void apiRequest<{ recommendations: Recommendation[]; notice: string }>(
      `/api/v1/demo/recommendations?platform=${platform}&exposure=${exposure}`,
    ).then((data) => {
      if (active) setRecommendations(data.recommendations);
    }).catch((cause) => {
      if (active) setError(cause instanceof Error ? cause.message : "演示数据暂时不可用。");
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [exposure, platform]);

  return (
    <div className="landing">
      <PublicHeader />
      <main>
        <section className="hero">
          <div className="hero__copy">
            <span className="eyebrow"><Radar size={15} /> 每天最多 3 个</span>
            <h1>先算<span>结算佣金</span><br />再决定挂什么</h1>
            <p>根据商品佣金、你的历史流量和已结算订单，估算一条内容可能获得的退款后佣金区间。</p>
            <div className="hero__actions">
              <a className="button button--primary" href="#demo">用演示数据试算 <ArrowRight size={17} /></a>
              <Link className="button button--ghost" to={viewer ? "/app/channels" : "/login"}>导入我的数据</Link>
            </div>
            <div className="trust-line"><ShieldCheck size={16} /> 不接商家排名费 · 不保证收益 · 每个数字可查看假设</div>
          </div>
          <div className="hero__visual">
            <div className="radar-orbit radar-orbit--outer" />
            <div className="radar-orbit radar-orbit--inner" />
            <div className="radar-sweep" />
            <div className="radar-dot radar-dot--one"><span>¥</span><small>佣金</small></div>
            <div className="radar-dot radar-dot--two"><span>3</span><small>精选</small></div>
            <div className="radar-dot radar-dot--three"><Check size={19} /><small>复核</small></div>
            <div className="radar-center"><Radar size={30} /><strong>三选</strong></div>
          </div>
        </section>

        <section className="principles" id="how">
          <div><span>01</span><h2>不是高佣榜</h2><p>高佣金遇上低成交或高退款，最后可能一分钱也结算不了。</p></div>
          <div><span>02</span><h2>按你的渠道算</h2><p>公众号阅读、B站播放和社群触达使用不同的冷启动假设。</p></div>
          <div><span>03</span><h2>佣金仍归你</h2><p>系统不转链、不替换 PID；你回官方联盟后台使用自己的推广位。</p></div>
        </section>

        <section className="demo-section" id="demo">
          <div className="section-heading">
            <div><span className="eyebrow">透明试算</span><h2>换一个渠道，看看结果怎么变</h2></div>
            <p>以下全部是合成演示数据，不对应真实商品、销量或佣金。</p>
          </div>
          <div className="demo-controls">
            <label>
              <span>内容渠道</span>
              <select value={platform} onChange={(event) => setPlatform(event.target.value as ChannelPlatform)}>
                {channelPlatforms.map((value) => <option key={value} value={value}>{channelLabels[value]}</option>)}
              </select>
            </label>
            <label>
              <span>{platform === "community" ? "实际触达人数" : "预计有效曝光"}</span>
              <input type="number" min={10} max={100_000_000} value={exposure} onChange={(event) => setExposure(Math.max(10, Number(event.target.value) || 10))} />
            </label>
            <div className="demo-controls__note"><Gauge size={17} /><span>无历史数据时使用低置信度默认参数</span></div>
          </div>
          {loading && <div className="loading-state"><span /><p>正在计算三个候选…</p></div>}
          {error && <div className="inline-error"><CircleAlert size={18} />{error}</div>}
          {!loading && !error && (
            <div className="product-grid">
              {recommendations.map((item) => <ProductCard key={item.productId} recommendation={item} />)}
            </div>
          )}
        </section>

        <section className="formula-section">
          <span className="eyebrow">固定公式，不让模型编数字</span>
          <h2>预计结算佣金</h2>
          <div className="formula">
            <strong>有效曝光</strong><span>×</span>
            <strong>点击率</strong><span>×</span>
            <strong>支付转化</strong><span>×</span>
            <strong>单笔佣金</strong><span>×</span>
            <strong>结算留存</strong>
          </div>
          <p>中转模型只解释受众匹配、内容角度和风险；价格、佣金、收益区间全部由程序计算。</p>
        </section>

        <section className="pricing" id="pricing">
          <div>
            <span className="eyebrow">创始版</span>
            <h2>先免费验证，再决定续不续</h2>
            <p>14 天完整试用。我们不抽你的联盟佣金，也不接受商家付费插队。</p>
          </div>
          <div className="price-card">
            <div><span>试用后</span><strong><small>¥</small>29</strong><span>/ 月</span></div>
            <ul>
              <li><Check size={16} /> 自动准备 5 个内容平台</li>
              <li><Check size={16} /> 每个账号每天最多 3 个候选</li>
              <li><Check size={16} /> 推广结果与结算复盘</li>
            </ul>
            <Link className="button button--primary" to={viewer ? "/app" : "/login"}>开始 14 天试用</Link>
          </div>
        </section>
      </main>
      <footer className="public-footer">
        <Brand />
        <p>结果为估算，不构成收益承诺。本工具不是任何内容平台或电商平台的官方产品。</p>
      </footer>
    </div>
  );
}

function LoginPage() {
  const { status, viewer, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(searchParams.has("error") ? "登录没有完成，请重试。" : "");
  const [authMode, setAuthMode] = useState<"signup" | "signin">("signup");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const state = location.state as { returnTo?: unknown } | null;
  const returnTo = typeof state?.returnTo === "string" && state.returnTo.startsWith("/app") ? state.returnTo : "/app";

  useEffect(() => {
    if (status === "authenticated" && viewer) void navigate(returnTo, { replace: true });
  }, [navigate, returnTo, status, viewer]);

  async function submitCredentials(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError("");
    try {
      const endpoint = authMode === "signup" ? "/api/auth/sign-up/email" : "/api/auth/sign-in/email";
      const response = await fetch(endpoint, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(authMode === "signup"
          ? { name, email, password, inviteCode }
          : { email, password }),
      });
      const payload: unknown = await response.json().catch(() => null);
      if (!response.ok) {
        const message = (payload as { message?: unknown; error?: { message?: unknown } } | null)?.message
          ?? (payload as { error?: { message?: unknown } } | null)?.error?.message;
        throw new Error(typeof message === "string" ? message : "邮箱或密码不正确，请检查后重试。");
      }
      await refresh();
      void navigate(returnTo, { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "登录暂时不可用。");
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-story">
        <Brand />
        <div>
          <span className="eyebrow"><Radar size={15} /> 创作者自己的收益雷达</span>
          <h1>每天少看一点，<br />每次多算一步。</h1>
          <p>只保存渠道画像和推广结果，不读取内容平台账号，不接管你的联盟佣金。</p>
        </div>
        <small><ShieldCheck size={15} /> 邀请制邮箱注册 · 14 天免费试用</small>
      </section>
      <section className="login-panel">
        <Link to="/" className="text-link">← 返回首页</Link>
        <div className="login-card">
          <span className="eyebrow">邀请制内测</span>
          <h2>开始建立你的基线</h2>
          <p>受邀创作者可用邮箱和邀请码注册，第一次注册会自动开始 14 天试用。</p>
          {error && <div className="inline-error"><CircleAlert size={17} />{error}</div>}
          <div className="auth-tabs">
            <button className={authMode === "signup" ? "active" : ""} type="button" onClick={() => setAuthMode("signup")}>注册</button>
            <button className={authMode === "signin" ? "active" : ""} type="button" onClick={() => setAuthMode("signin")}>登录</button>
          </div>
          <form className="credential-form" onSubmit={(event) => void submitCredentials(event)}>
            {authMode === "signup" && <label><span>你的称呼</span><input required minLength={1} maxLength={60} autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} placeholder="例如：小林" /></label>}
            <label><span>邮箱</span><input required type="email" maxLength={160} autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@example.com" /></label>
            <label><span>密码</span><input required type="password" minLength={8} maxLength={128} autoComplete={authMode === "signup" ? "new-password" : "current-password"} value={password} onChange={(event) => setPassword(event.target.value)} placeholder="至少 8 位" /></label>
            {authMode === "signup" && <label><span>内测邀请码</span><input required minLength={8} maxLength={128} autoComplete="off" value={inviteCode} onChange={(event) => setInviteCode(event.target.value)} placeholder="由站点管理员提供" /></label>}
            <button className="button button--primary button--full" disabled={pending}>{pending ? "请稍候…" : authMode === "signup" ? "注册并开始试用" : "登录雷达"} <ArrowRight size={17} /></button>
          </form>
          <div className="login-points">
            <span><Check size={14} /> 密码由认证组件加密保存</span>
            <span><Check size={14} /> 不连接内容平台账号</span>
            <span><Check size={14} /> 不替换推广链接</span>
          </div>
        </div>
      </section>
    </main>
  );
}

function Protected({ children }: PropsWithChildren) {
  const { status, viewer, refresh } = useAuth();
  const location = useLocation();
  if (status === "loading") return <main className="full-state"><Brand /><span className="spinner" /><p>正在打开你的雷达…</p></main>;
  if (status === "error") return <main className="full-state"><Brand /><CircleAlert size={28} /><h1>暂时无法检查登录状态</h1><button className="button" onClick={() => void refresh()}>重试</button></main>;
  if (!viewer) return <Navigate to="/login" replace state={{ returnTo: location.pathname }} />;
  return children;
}

const appNav = [
  { to: "/app", label: "今日推荐", icon: LayoutDashboard, end: true },
  { to: "/app/channels", label: "我的渠道", icon: UserRoundCog },
  { to: "/app/outcomes", label: "推广记录", icon: BarChart3 },
  { to: "/app/subscription", label: "订阅", icon: WalletCards },
] as const;

function AppShell({ children }: PropsWithChildren) {
  const { viewer, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  if (!viewer) return null;

  return (
    <div className="app-layout">
      <aside className={open ? "sidebar sidebar--open" : "sidebar"}>
        <div className="sidebar__top"><Brand /><button className="icon-button sidebar__close" onClick={() => setOpen(false)}><X size={18} /></button></div>
        <nav>
          {appNav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} end={"end" in item ? item.end : false} onClick={() => setOpen(false)}>
                <Icon size={18} />{item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="trial-chip">
          <Gauge size={17} />
          <div><strong>{viewer.subscription.status === "active" ? "创始版" : `试用剩余 ${viewer.subscription.daysRemaining} 天`}</strong><small>佣金全部归创作者</small></div>
        </div>
        <div className="sidebar__account">
          {viewer.user.image ? <img src={viewer.user.image} alt="" referrerPolicy="no-referrer" /> : <span>{viewer.user.name.slice(0, 1)}</span>}
          <div><strong>{viewer.user.name}</strong><small>{viewer.user.email}</small></div>
          <button className="icon-button" title="退出登录" onClick={() => void signOut()}><LogOut size={17} /></button>
        </div>
      </aside>
      {open && <button className="sidebar-backdrop" aria-label="关闭导航" onClick={() => setOpen(false)} />}
      <main className="app-main">
        <header className="mobile-header"><button className="icon-button" onClick={() => setOpen(true)}><Menu size={20} /></button><Brand /></header>
        {children}
      </main>
    </div>
  );
}

function PageHeading({ eyebrow, title, description, action }: { eyebrow: string; title: string; description: string; action?: ReactNode }) {
  return (
    <div className="page-heading">
      <div><span className="eyebrow">{eyebrow}</span><h1>{title}</h1><p>{description}</p></div>
      {action}
    </div>
  );
}

function isChannelProfileReady(profile: ChannelProfile): boolean {
  return profile.niche.trim().length >= 2
    && profile.audienceSummary.trim().length >= 2
    && profile.averageExposure >= 10;
}

function DashboardPage() {
  const [profiles, setProfiles] = useState<ChannelProfile[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [recommendationRequest, setRecommendationRequest] = useState<{ profileId: string | null; nonce: number }>({
    profileId: null,
    nonce: 0,
  });
  const [payload, setPayload] = useState<RecommendationPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingProduct, setPendingProduct] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    void apiRequest<{ profiles: ChannelProfile[] }>("/api/v1/channel-profiles")
      .then((data) => {
        if (!active) return;
        setProfiles(data.profiles);
        setSelectedId((current) => current || data.profiles[0]?.id || "");
      })
      .catch((cause) => {
        if (active) setError(cause instanceof Error ? cause.message : "渠道资料读取失败。");
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    const query = recommendationRequest.profileId
      ? `?channelProfileId=${recommendationRequest.profileId}`
      : "";
    void apiRequest<RecommendationPayload>(`/api/v1/recommendations${query}`)
      .then((data) => {
        if (!active) return;
        setPayload(data);
        if (data.profile && data.profile.id !== selectedId) setSelectedId(data.profile.id);
      })
      .catch((cause) => {
        if (!active) return;
        setError(cause instanceof Error ? cause.message : "推荐暂时不可用。");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [recommendationRequest]);

  async function handoff(recommendation: Recommendation) {
    setPendingProduct(recommendation.productId);
    setNotice("");
    setError("");
    try {
      const data = await apiRequest<{
        itemId: string;
        destinationUrl: string | null;
        demo: boolean;
        notice: string;
      }>(`/api/v1/products/${recommendation.productId}/handoff`, {
        method: "POST",
        body: JSON.stringify({ channelProfileId: recommendation.channelProfile.id }),
      });
      if (!data.demo) {
        let copied = false;
        if (navigator.clipboard) {
          try {
            await navigator.clipboard.writeText(data.itemId);
            copied = true;
          } catch {
            copied = false;
          }
        }
        if (!copied) {
          const acknowledged = window.prompt("自动复制失败，请手动复制商品 ID 后继续：", data.itemId);
          if (acknowledged === null) {
            setNotice(`候选已记录。商品 ID：${data.itemId}`);
            return;
          }
        }
      }
      setNotice(data.notice);
      if (data.destinationUrl) window.location.assign(data.destinationUrl);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "无法记录候选。");
    } finally {
      setPendingProduct("");
    }
  }

  const selected = profiles.find((profile) => profile.id === selectedId);
  const selectedReady = selected ? isChannelProfileReady(selected) : false;

  return (
    <AppShell>
      <PageHeading
        eyebrow="今日推荐"
        title="今天只看 3 个"
        description="先看预计结算佣金，再决定要不要花时间做内容。"
        action={profiles.length > 0 ? (
          <label className="profile-picker">
            <span>{payload?.dailyLocked ? "今日已锁定渠道" : "当前渠道"}</span>
            <select value={selectedId} disabled={payload?.dailyLocked} onChange={(event) => setSelectedId(event.target.value)}>
              {profiles.map((profile) => <option key={profile.id} value={profile.id}>{channelLabels[profile.platform]} · {profile.displayName}</option>)}
            </select>
          </label>
        ) : undefined}
      />

      {payload?.catalogMode === "demo" && (
        <div className="notice-banner notice-banner--demo"><CircleAlert size={18} /><span><strong>当前是演示目录。</strong>所有商品、销量和佣金均为合成数据，不能用于真实推广。</span></div>
      )}
      {selected && !selected.officialComponentConfirmed && (
        <div className="notice-banner"><ShieldCheck size={18} /><span>你还没有确认该渠道的商品组件或外链资格；推荐卡会保持“渠道待确认”。</span></div>
      )}
      {payload?.dailyLocked && profiles.length > 1 && (
        <div className="notice-banner"><Gauge size={18} /><span>今天的 3 个候选已按“{payload.profile?.displayName}”生成；明天可以改用其他渠道，避免反复刷新挑选。</span></div>
      )}
      {notice && <div className="notice-banner notice-banner--success"><Check size={18} />{notice}</div>}
      {error && <div className="inline-error"><CircleAlert size={18} />{error}</div>}

      {!loading && profiles.length === 0 && (
        <section className="empty-panel">
          <UserRoundCog size={35} />
          <h2>五个平台暂时没有准备好</h2>
          <p>系统会自动建立公众号、知乎、B站、微博和社群画像，不需要手动新增。</p>
          <button className="button button--primary" onClick={() => window.location.reload()}>重新准备 <ArrowRight size={16} /></button>
        </section>
      )}

      {!loading && profiles.length > 0 && payload?.awaitingProfileSelection && (
        <section className="empty-panel">
          <Radar size={35} />
          <h2>{selectedReady ? "选择今天要服务的渠道" : `先完善${selected ? channelLabels[selected.platform] : "渠道"}画像`}</h2>
          <p>{selectedReady ? "确认后才会生成并锁定今天的 3 个候选，切换下拉框不会消耗当日名额。" : "平台已经自动建立；补充真实赛道、受众和平均流量后，才能给出不误导的收益估算。"}</p>
          {selectedReady ? (
            <button
              className="button button--primary"
              disabled={!selectedId}
              onClick={() => setRecommendationRequest({ profileId: selectedId, nonce: Date.now() })}
            >
              按此渠道生成今天 3 个 <ArrowRight size={16} />
            </button>
          ) : (
            <Link className="button button--primary" to={`/app/channels?profile=${selectedId}`}>去完善真实数据 <ArrowRight size={16} /></Link>
          )}
        </section>
      )}

      {loading && <div className="loading-state"><span /><p>正在按你的渠道计算…</p></div>}
      {!loading && payload && profiles.length > 0 && !payload.awaitingProfileSelection && payload.recommendations.length === 0 && !error && (
        <section className="empty-panel">
          <PackageCheck size={35} />
          <h2>今天没有达到门槛的商品</h2>
          <p>宁可少推荐，也不拿高佣金、低质量或过期商品凑数。</p>
        </section>
      )}
      {!loading && payload && (
        <div className="product-grid product-grid--dashboard">
          {payload.recommendations.map((item) => (
            <ProductCard
              key={item.productId}
              recommendation={item}
              actionLabel={item.dataOrigin === "synthetic_demo" ? "记录演示候选" : "复制 ID，去联盟转链"}
              onAction={(recommendation) => void handoff(recommendation)}
              pending={pendingProduct === item.productId}
            />
          ))}
        </div>
      )}
    </AppShell>
  );
}

function profileToInput(profile: ChannelProfile): ChannelProfileInput {
  return {
    id: profile.id,
    platform: profile.platform,
    displayName: profile.displayName,
    niche: profile.niche,
    audienceSummary: profile.audienceSummary,
    averageExposure: profile.averageExposure,
    sampleBudgetCents: profile.sampleBudgetCents,
    contentCostCents: profile.contentCostCents,
    officialComponentConfirmed: profile.officialComponentConfirmed,
  };
}

function ChannelsPage() {
  const [searchParams] = useSearchParams();
  const [profiles, setProfiles] = useState<ChannelProfile[]>([]);
  const [form, setForm] = useState<ChannelProfileInput | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  async function load(preferredId?: string) {
    const data = await apiRequest<{ profiles: ChannelProfile[] }>("/api/v1/channel-profiles");
    setProfiles(data.profiles);
    setForm((current) => {
      const selectedId = preferredId ?? current?.id;
      const selected = data.profiles.find((profile) => profile.id === selectedId)
        ?? data.profiles[0];
      return selected ? profileToInput(selected) : null;
    });
  }

  useEffect(() => {
    void load(searchParams.get("profile") ?? undefined)
      .catch((cause) => setError(cause instanceof Error ? cause.message : "渠道读取失败。"));
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const saved = await apiRequest<ChannelProfile>("/api/v1/channel-profiles", {
        method: "POST",
        body: JSON.stringify(form),
      });
      setNotice(`${channelLabels[saved.platform]}画像已更新。`);
      await load(saved.id);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "渠道保存失败。");
    } finally {
      setSaving(false);
    }
  }

  function edit(profile: ChannelProfile) {
    setForm(profileToInput(profile));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <AppShell>
      <PageHeading eyebrow="我的渠道" title="五个平台已经准备好" description="不需要逐个新增；只需把默认值改成你的真实赛道、受众和平均流量。" />
      <div className="notice-banner notice-banner--success"><Check size={18} /><span><strong>系统已自动准备公众号、知乎、B站、微博和社群。</strong>这里只保存画像，不连接或操作平台账号。</span></div>
      {notice && <div className="notice-banner notice-banner--success"><Check size={18} />{notice}</div>}
      {error && <div className="inline-error"><CircleAlert size={18} />{error}</div>}
      <div className="two-column-layout">
        {form ? (
          <form className="panel form-panel" onSubmit={(event) => void submit(event)}>
            <div className="panel__heading"><div><span className="eyebrow">{form.niche && form.audienceSummary && form.averageExposure >= 10 ? "编辑" : "待完善"} {channelLabels[form.platform]}</span><h2>更新真实流量基线</h2></div></div>
            <label><span>内容平台</span><select value={form.platform} disabled><option value={form.platform}>{channelLabels[form.platform]}</option></select><small>平台已由系统建立，无需新增。</small></label>
            <label><span>渠道名称</span><input required maxLength={60} value={form.displayName} onChange={(event) => setForm({ ...form, displayName: event.target.value })} placeholder="例如：我的效率手册" /></label>
            <label><span>内容赛道</span><input required maxLength={80} value={form.niche} onChange={(event) => setForm({ ...form, niche: event.target.value })} placeholder="例如：桌面效率、数码工具" /></label>
            <label><span>核心受众</span><textarea required maxLength={240} value={form.audienceSummary} onChange={(event) => setForm({ ...form, audienceSummary: event.target.value })} placeholder="例如：关注远程办公和轻量工具的职场人" /></label>
            <div className="form-row">
              <label><span>{form.platform === "community" ? "平均实际触达" : "平均有效曝光"}</span><input type="number" required min={10} max={100_000_000} value={form.averageExposure} onChange={(event) => setForm({ ...form, averageExposure: Number(event.target.value) })} /></label>
              <label><span>单件样品预算（元）</span><input type="number" min={0} max={300} step="1" value={form.sampleBudgetCents / 100} onChange={(event) => setForm({ ...form, sampleBudgetCents: Math.round(Number(event.target.value) * 100) })} /></label>
            </div>
            <label><span>每条内容明确成本（元）</span><input type="number" min={0} max={1000} step="1" value={form.contentCostCents / 100} onChange={(event) => setForm({ ...form, contentCostCents: Math.round(Number(event.target.value) * 100) })} /><small>不填时间成本时，商品卡会明确提示“未计创作时间成本”。</small></label>
            <label className="checkbox-row"><input type="checkbox" checked={form.officialComponentConfirmed} onChange={(event) => setForm({ ...form, officialComponentConfirmed: event.target.checked })} /><span><strong>我已在平台官方规则中确认自己的商品组件或推广方式可用</strong><small>系统不会替你判断账号权限，也不会指导绕过外链规则。</small></span></label>
            <button className="button button--primary button--full" disabled={saving}>{saving ? "保存中…" : "保存修改"} <ArrowRight size={16} /></button>
          </form>
        ) : (
          <section className="panel loading-state"><span /><p>正在准备五个平台…</p></section>
        )}
        <section className="panel">
          <div className="panel__heading"><div><span className="eyebrow">选择要修改的平台</span><h2>{profiles.length} 个渠道画像</h2></div></div>
          <div className="channel-list">
            {profiles.length === 0 && <div className="list-empty"><UserRoundCog size={28} /><p>正在自动建立渠道画像。</p></div>}
            {profiles.map((profile) => (
              <article key={profile.id} className="channel-item">
                <span className="channel-item__icon">{channelLabels[profile.platform].slice(0, 1)}</span>
                <div><div className="tag-row"><span className="tag tag--muted">{channelLabels[profile.platform]}</span><span className={isChannelProfileReady(profile) ? "tag tag--live" : "tag tag--demo"}>{isChannelProfileReady(profile) ? "可使用" : "待完善"}</span>{profile.officialComponentConfirmed && <span className="tag tag--live">已自检组件</span>}</div><h3>{profile.displayName}</h3><p>{profile.niche || "尚未填写内容赛道"}</p><small>{isChannelProfileReady(profile) ? `${profile.averageExposure.toLocaleString("zh-CN")} ${profile.platform === "community" ? "平均实际触达" : "平均有效曝光"}` : "平均流量待填写"} · 样品上限 {formatCurrency(profile.sampleBudgetCents)}</small></div>
                <div className="channel-item__actions"><button className="text-button" onClick={() => edit(profile)}>修改</button></div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </AppShell>
  );
}

function OutcomesPage() {
  const [handoffs, setHandoffs] = useState<Handoff[]>([]);
  const [outcomes, setOutcomes] = useState<OutcomeRecord[]>([]);
  const [summary, setSummary] = useState<OutcomeSummary | null>(null);
  const [selectedHandoffId, setSelectedHandoffId] = useState("");
  const [status, setStatus] = useState<OutcomeRecord["status"]>("candidate");
  const [actualExposure, setActualExposure] = useState("0");
  const [clicks, setClicks] = useState("0");
  const [paidOrders, setPaidOrders] = useState("0");
  const [settledOrders, setSettledOrders] = useState("0");
  const [settledCommission, setSettledCommission] = useState("0");
  const [sampleCost, setSampleCost] = useState("0");
  const [contentCost, setContentCost] = useState("0");
  const [contentUrl, setContentUrl] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  async function load() {
    const [handoffData, outcomeData, summaryData] = await Promise.all([
      apiRequest<{ handoffs: Handoff[] }>("/api/v1/handoffs"),
      apiRequest<{ outcomes: OutcomeRecord[] }>("/api/v1/outcomes"),
      apiRequest<OutcomeSummary>("/api/v1/outcomes/summary"),
    ]);
    setHandoffs(handoffData.handoffs);
    setOutcomes(outcomeData.outcomes);
    setSummary(summaryData);
    setSelectedHandoffId((current) => current || handoffData.handoffs[0]?.id || "");
  }

  useEffect(() => {
    void load().catch((cause) => setError(cause instanceof Error ? cause.message : "推广记录读取失败。"));
  }, []);

  const existing = outcomes.find((outcome) => outcome.handoffEventId === selectedHandoffId);

  useEffect(() => {
    if (!existing) {
      setStatus("candidate");
      setActualExposure("0");
      setClicks("0");
      setPaidOrders("0");
      setSettledOrders("0");
      setSettledCommission("0");
      setSampleCost("0");
      setContentCost("0");
      setContentUrl("");
      return;
    }
    setStatus(existing.status);
    setActualExposure(String(existing.actualExposure));
    setClicks(String(existing.clicks));
    setPaidOrders(String(existing.paidOrders));
    setSettledOrders(String(existing.settledOrders));
    setSettledCommission(String(existing.settledCommissionCents / 100));
    setSampleCost(String(existing.sampleCostCents / 100));
    setContentCost(String(existing.contentCostCents / 100));
    setContentUrl(existing.contentUrl ?? "");
  }, [existing]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setNotice("");
    try {
      await apiRequest<OutcomeRecord>("/api/v1/outcomes", {
        method: "POST",
        body: JSON.stringify({
          id: existing?.id,
          handoffEventId: selectedHandoffId,
          status,
          contentUrl: contentUrl || null,
          publishedAt: status === "candidate" ? null : existing?.publishedAt ?? Date.now(),
          actualExposure: Number(actualExposure),
          clicks: Number(clicks),
          paidOrders: Number(paidOrders),
          settledOrders: Number(settledOrders),
          settledCommissionCents: Math.round(Number(settledCommission) * 100),
          sampleCostCents: Math.round(Number(sampleCost) * 100),
          contentCostCents: Math.round(Number(contentCost) * 100),
        }),
      });
      setNotice("推广结果已保存，下一次推荐会优先使用已结算数据。");
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "推广结果保存失败。");
    }
  }

  return (
    <AppShell>
      <PageHeading eyebrow="推广记录" title="把预估和真实结算分开" description="只有平台最终结算的佣金才进入贡献收益；演示商品不会计入真实收益。" />
      {notice && <div className="notice-banner notice-banner--success"><Check size={18} />{notice}</div>}
      {error && <div className="inline-error"><CircleAlert size={18} />{error}</div>}
      {summary && (
        <div className="summary-grid">
          <div><ClipboardCheck size={18} /><span>候选记录</span><strong>{summary.promotedCount}</strong></div>
          <div><Eye size={18} /><span>真实曝光</span><strong>{summary.totalExposure.toLocaleString("zh-CN")}</strong></div>
          <div><PackageCheck size={18} /><span>结算订单</span><strong>{summary.settledOrders}</strong></div>
          <div className="summary-grid__money"><CircleDollarSign size={18} /><span>真实贡献</span><strong>{formatCurrency(summary.contributionCents)}</strong></div>
        </div>
      )}
      {handoffs.length === 0 ? (
        <section className="empty-panel"><ReceiptText size={35} /><h2>还没有候选记录</h2><p>在“今日推荐”中选择商品后，这里才会出现可复盘的记录。</p><Link className="button button--primary" to="/app">查看今日推荐</Link></section>
      ) : (
        <div className="two-column-layout">
          <form className="panel form-panel" onSubmit={(event) => void submit(event)}>
            <div className="panel__heading"><div><span className="eyebrow">记录结果</span><h2>{existing ? "更新真实数据" : "建立一条结果记录"}</h2></div></div>
            <label><span>候选商品</span><select value={selectedHandoffId} onChange={(event) => setSelectedHandoffId(event.target.value)}>{handoffs.map((handoff) => <option key={handoff.id} value={handoff.id}>{handoff.productTitle} · {handoff.channelName}{handoff.dataOrigin === "synthetic_demo" ? "（演示）" : ""}</option>)}</select></label>
            <label><span>当前状态</span><select value={status} onChange={(event) => setStatus(event.target.value as OutcomeRecord["status"])}><option value="candidate">只加入候选</option><option value="published">已经发布</option><option value="settled">已经结算</option><option value="cancelled">已放弃</option></select></label>
            <label><span>内容地址（可选）</span><input type="url" value={contentUrl} onChange={(event) => setContentUrl(event.target.value)} placeholder="https://" /></label>
            <div className="form-row"><label><span>有效曝光</span><input type="number" min={0} value={actualExposure} onChange={(event) => setActualExposure(event.target.value)} /></label><label><span>点击数</span><input type="number" min={0} value={clicks} onChange={(event) => setClicks(event.target.value)} /></label></div>
            <div className="form-row"><label><span>支付订单</span><input type="number" min={0} value={paidOrders} onChange={(event) => setPaidOrders(event.target.value)} /></label><label><span>结算订单</span><input type="number" min={0} value={settledOrders} onChange={(event) => setSettledOrders(event.target.value)} /></label></div>
            <label><span>最终结算佣金（元）</span><input type="number" min={0} step="0.01" value={settledCommission} onChange={(event) => setSettledCommission(event.target.value)} /></label>
            <div className="form-row"><label><span>样品成本（元）</span><input type="number" min={0} step="0.01" value={sampleCost} onChange={(event) => setSampleCost(event.target.value)} /></label><label><span>内容成本（元）</span><input type="number" min={0} step="0.01" value={contentCost} onChange={(event) => setContentCost(event.target.value)} /></label></div>
            <button className="button button--primary button--full">保存结果 <ArrowRight size={16} /></button>
          </form>
          <section className="panel">
            <div className="panel__heading"><div><span className="eyebrow">最近记录</span><h2>预估之后发生了什么</h2></div></div>
            <div className="outcome-list">
              {outcomes.length === 0 && <div className="list-empty"><BarChart3 size={28} /><p>选择商品后记录真实结果，系统才会逐步替换默认参数。</p></div>}
              {outcomes.map((outcome) => (
                <article key={outcome.id}>
                  <div><span className={`status-dot status-dot--${outcome.status}`} /> <strong>{outcome.productTitle}{outcome.dataOrigin === "synthetic_demo" ? "（演示）" : ""}</strong><small>{outcome.channelName} · {formatDate(outcome.updatedAt)}</small></div>
                  <dl><div><dt>曝光</dt><dd>{outcome.actualExposure.toLocaleString("zh-CN")}</dd></div><div><dt>支付</dt><dd>{outcome.paidOrders}</dd></div><div><dt>结算</dt><dd>{outcome.settledOrders}</dd></div><div><dt>佣金</dt><dd>{formatCurrency(outcome.settledCommissionCents)}</dd></div></dl>
                </article>
              ))}
            </div>
          </section>
        </div>
      )}
    </AppShell>
  );
}

function SubscriptionPage() {
  const { viewer } = useAuth();
  const [contact, setContact] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  if (!viewer) return null;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setNotice("");
    try {
      const data = await apiRequest<{ status: string; notice: string }>("/api/v1/subscription/interest", {
        method: "POST",
        body: JSON.stringify({ plan: "founder", contact }),
      });
      setNotice(data.notice);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "登记失败。");
    }
  }

  return (
    <AppShell>
      <PageHeading eyebrow="订阅" title="佣金归你，工具按月收费" description="MVP 暂不接入自动扣款；先记录真实续费意向，再决定国内收款方案。" />
      {notice && <div className="notice-banner notice-banner--success"><Check size={18} />{notice}</div>}
      {error && <div className="inline-error"><CircleAlert size={18} />{error}</div>}
      <div className="subscription-layout">
        <section className="subscription-status">
          <span className="eyebrow">当前状态</span>
          <div className="subscription-status__ring"><strong>{viewer.subscription.status === "active" ? "已开通" : viewer.subscription.daysRemaining}</strong><small>{viewer.subscription.status === "active" ? "创始版" : "天试用"}</small></div>
          <h2>{viewer.subscription.status === "expired" ? "试用已结束" : "先用真实数据验证价值"}</h2>
          <p>如果产品没有帮你少选错商品、提高结算后佣金，就不应该续费。</p>
        </section>
        <section className="panel founder-plan">
          <div className="founder-plan__price"><div><span className="eyebrow">创始版</span><h2>¥29 <small>/ 月</small></h2></div><span className="tag tag--live">佣金 100% 归创作者</span></div>
          <ul><li><BadgeCheck size={17} /> 自动准备 5 个渠道画像</li><li><BadgeCheck size={17} /> 每个账号每天最多 3 个候选</li><li><BadgeCheck size={17} /> 透明收益假设和结算复盘</li><li><BadgeCheck size={17} /> 不接受商家付费插队</li></ul>
          <form onSubmit={(event) => void submit(event)}><label><span>留下微信号或邮箱</span><input required minLength={2} maxLength={120} value={contact} onChange={(event) => setContact(event.target.value)} placeholder="仅用于确认创始版意向" /></label><button className="button button--primary button--full">登记 ¥29 续费意向</button></form>
          <small>此按钮不会扣款。正式收款前会再次明确价格、周期和退款方式。</small>
        </section>
      </div>
    </AppShell>
  );
}

function NotFoundPage() {
  return <main className="full-state"><Brand /><h1>这个页面不在雷达范围内</h1><Link className="button button--primary" to="/">返回首页</Link></main>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/app" element={<Protected><DashboardPage /></Protected>} />
      <Route path="/app/channels" element={<Protected><ChannelsPage /></Protected>} />
      <Route path="/app/outcomes" element={<Protected><OutcomesPage /></Protected>} />
      <Route path="/app/subscription" element={<Protected><SubscriptionPage /></Protected>} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
